import {
  Component,
  computed,
  DestroyRef,
  inject,
  Signal,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  GcButtonComponent,
  GcInputFieldComponent,
  GcTextComponent,
  IpoTranslatePipe,
} from '@ip-only/front-end-library';
import { GcHeaderComponent } from '../shared/gc-header/gc-header.component';
import {
  getTopLevelDomain,
  isTopLevelDomainFI,
} from '../shared/util/deploy-helper';
import { UserService } from '../user/user.service';
import { GuideDocumentCardComponent } from './guide-document-card/guide-document-card.component';
import {
  GUIDE_VARIANTS,
  GUIDES_BY_VARIANT,
  GuideSection,
  GuideVariant,
  PortalCountry,
} from './guide-document.entities';

@Component({
  selector: 'app-guide-document',
  imports: [
    GuideDocumentCardComponent,
    GcTextComponent,
    GcInputFieldComponent,
    GcButtonComponent,
    IpoTranslatePipe,
    GcHeaderComponent,
  ],
  templateUrl: './guide-document.component.html',
  styleUrl: './guide-document.component.scss',
})
export class GuideDocumentComponent {
  private readonly userService = inject(UserService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly isInternalUser = signal(false);
  readonly searchInputString = signal('');
  readonly guideSections = signal<GuideSection[]>(this.createGuideSections());

  readonly topLevelDomain = getTopLevelDomain();
  isDeliveryPortalFI = isTopLevelDomainFI();

  readonly visibleGuideSections = computed<GuideSection[]>(() =>
    this.createVisibleGuideSections(),
  );

  readonly noResultsFound: Signal<boolean> = computed<boolean>(
    () =>
      this.searchInputString().length > 0 &&
      this.visibleGuideSections().length === 0,
  );

  constructor() {
    this.subscribeToPortalUser();
  }

  private subscribeToPortalUser(): void {
    this.userService
      .getPortalUser$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        this.isInternalUser.set(user?.federatedUser === true);
      });
  }

  private isSectionVisibleForUserAndCountry(section: GuideSection): boolean {
    const isSameCountry = section.country === this.getCurrentPortalCountry();
    const isAllowedForUser = this.isInternalUser() || !section.isInternal;

    return isSameCountry && isAllowedForUser;
  }

  private createVisibleGuideSections(): GuideSection[] {
    const sectionVisibleForUserAndCountry = this.guideSections().filter(
      (section) => this.isSectionVisibleForUserAndCountry(section),
    );

    return this.getSectionsWithGuidesMatchingSearchTerm(
      sectionVisibleForUserAndCountry,
      this.searchInputString(),
    );
  }

  private createGuideSections(): GuideSection[] {
    return Object.entries(GUIDE_VARIANTS).map(([key, value]) => {
      const variant = key as GuideVariant;

      return {
        key: variant,
        label: value.label,
        country: value.country,
        isInternal: value.isInternal,
        guides: GUIDES_BY_VARIANT[variant],
      };
    });
  }

  private getSectionsWithGuidesMatchingSearchTerm(
    sections: GuideSection[],
    searchTerm: string,
  ): GuideSection[] {
    return sections
      .map((section) =>
        this.filterSectionGuidesBySearchTerm(section, searchTerm),
      )
      .filter((section) => section.guides.length > 0);
  }

  private getCurrentPortalCountry(): PortalCountry {
    return this.isDeliveryPortalFI ? 'FI' : 'SE';
  }

  private filterSectionGuidesBySearchTerm(
    section: GuideSection,
    searchTerm: string,
  ): GuideSection {
    if (!searchTerm) {
      return section;
    }

    const guidesMatchingSearchTerm = section.guides.filter((guide) =>
      guide.title.toLowerCase().includes(searchTerm),
    );

    return {
      ...section,
      guides: guidesMatchingSearchTerm,
    };
  }

  setSearchInputString(newTerm: string): void {
    this.searchInputString.set(newTerm.trim().toLowerCase());
  }

  goToHomePage() {
    this.router.navigate(['/home']);
  }
}
